import { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, AlertCircle, CheckCircle, XCircle, Eye, X, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import './LegalAgreementsPage.css';

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
    });
};

const LegalAgreementsPage = () => {
    const [acceptances, setAcceptances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewContent, setViewContent] = useState(null);

    const fetchAcceptances = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.get(API_ENDPOINTS.LEGAL_AGREEMENT.MY_ACCEPTANCES);
            if (res.success) {
                setAcceptances(res.data || []);
            } else {
                setError(res.error || 'Veriler yüklenemedi');
            }
        } catch {
            setError('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAcceptances();
    }, [fetchAcceptances]);

    if (loading && acceptances.length === 0) {
        return (
            <div className="la-container">
                <div className="page-loading">
                    <Loader2 className="spinning" size={40} />
                    <span>Yükleniyor...</span>
                </div>
            </div>
        );
    }

    const active = acceptances.filter(a => !a.isWithdrawn);
    const withdrawn = acceptances.filter(a => a.isWithdrawn);

    return (
        <div className="la-container">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><Shield size={20} /></div>
                    <div className="page-title">
                        <h1>Sözleşmelerim</h1>
                        <p>Kabul ettiğiniz yasal sözleşmeler</p>
                    </div>
                </div>
                <button className="btn btn-secondary" onClick={fetchAcceptances} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                    Yenile
                </button>
            </div>

            {error && (
                <div className="error-banner">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                </div>
            )}

            {acceptances.length === 0 && !loading ? (
                <div className="page-empty">
                    <Shield size={48} />
                    <h3>Sözleşme Bulunamadı</h3>
                    <p>Henüz kabul edilmiş bir sözleşmeniz bulunmuyor.</p>
                </div>
            ) : (
                <>
                    {active.length > 0 && (
                        <div className="la-section">
                            <h2 className="la-section-title">
                                <CheckCircle size={16} />
                                Aktif Sözleşmeler ({active.length})
                            </h2>
                            <div className="la-grid">
                                {active.map((a) => (
                                    <div key={a.id} className="la-card active clickable" onClick={() => setViewContent(a)}>
                                        <div className="la-card-top">
                                            <div>
                                                <h3>{a.templateTitle || 'Sözleşme'}</h3>
                                                {a.templateCode && <span className="la-code">{a.templateCode}</span>}
                                            </div>
                                            <span className="la-badge active">
                                                <CheckCircle size={13} />
                                                Kabul Edildi
                                            </span>
                                        </div>
                                        <div className="la-card-details">
                                            <div className="la-detail-row">
                                                <span className="la-detail-label">Versiyon</span>
                                                <span className="la-detail-value">{a.versionNumber || '-'}</span>
                                            </div>
                                            <div className="la-detail-row">
                                                <span className="la-detail-label">Kabul Tarihi</span>
                                                <span className="la-detail-value">{formatDate(a.createdAtUtc)}</span>
                                            </div>
                                        </div>
                                        <div className="la-view-hint">
                                            <Eye size={14} />
                                            Sözleşmeyi Görüntüle
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {withdrawn.length > 0 && (
                        <div className="la-section">
                            <h2 className="la-section-title withdrawn">
                                <XCircle size={16} />
                                Geri Çekilen ({withdrawn.length})
                            </h2>
                            <div className="la-grid">
                                {withdrawn.map((a) => (
                                    <div key={a.id} className="la-card withdrawn clickable" onClick={() => setViewContent(a)}>
                                        <div className="la-card-top">
                                            <div>
                                                <h3>{a.templateTitle || 'Sözleşme'}</h3>
                                                {a.templateCode && <span className="la-code">{a.templateCode}</span>}
                                            </div>
                                            <span className="la-badge withdrawn">
                                                <XCircle size={13} />
                                                Geri Çekildi
                                            </span>
                                        </div>
                                        <div className="la-card-details">
                                            <div className="la-detail-row">
                                                <span className="la-detail-label">Versiyon</span>
                                                <span className="la-detail-value">{a.versionNumber || '-'}</span>
                                            </div>
                                            <div className="la-detail-row">
                                                <span className="la-detail-label">Kabul Tarihi</span>
                                                <span className="la-detail-value">{formatDate(a.createdAtUtc)}</span>
                                            </div>
                                            {a.withdrawnAt && (
                                                <div className="la-detail-row">
                                                    <span className="la-detail-label">Çekilme Tarihi</span>
                                                    <span className="la-detail-value">{formatDate(a.withdrawnAt)}</span>
                                                </div>
                                            )}
                                            {a.withdrawalReason && (
                                                <div className="la-detail-row">
                                                    <span className="la-detail-label">Sebep</span>
                                                    <span className="la-detail-value">{a.withdrawalReason}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="la-view-hint">
                                            <Eye size={14} />
                                            Sözleşmeyi Görüntüle
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {viewContent && (
                <div className="modal-overlay" onClick={() => setViewContent(null)}>
                    <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{viewContent.templateTitle}</h2>
                            <button className="modal-close" onClick={() => setViewContent(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-body" dangerouslySetInnerHTML={{ __html: viewContent.contentHtml }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegalAgreementsPage;
